import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {useCommonItems} from "../../../../../contexts/CommonItensProvider";
import {Controller, useForm} from "react-hook-form";
import {listaSistemas, manipulaChangelog} from "../../configs/functions";
import {getFormErrorMessage} from "../../../../../configs/functions";
import {useEffect, useMemo, useRef, useState} from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    TextField,
    MenuItem,
    Button,
    Typography,
    IconButton,
} from "@mui/material";
import {CommonDialogActionsForm} from "../../../../common/CommonDialogActionsForm";
import dayjs from "dayjs";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {Delete, Visibility, InsertDriveFile, ExpandMore, Palette} from "@mui/icons-material";
import {grey} from "@mui/material/colors";
import {NotificationCard, TIPOS_NOTIFICACAO} from "../../../../common/NotificationCard";
import {EditorTexto} from "../../../../common/Notification/components/EditorTexto";
import {Aparencia} from "../../../../common/Notification/components/Aparencia";

function temPersonalizacao(estilo) {
    return !!(estilo?.cor || estilo?.emoji || estilo?.rotulo || estilo?.chips?.length);
}

export function ChangelogForm({
                                  open,
                                  previewOpen,
                                  setPreviewOpen,
                                  setOpen,
                                  changelog,
                                  queryKey
                              }) {
    const queryClient = useQueryClient();
    const { exibirAlerta } = useCommonItems();
    const fileInputRef = useRef(null);

    const ultimaAbertura = useRef(null);

    const [selectedFile, setSelectedFile] = useState(null);
    const [existingFile, setExistingFile] = useState(null);
    const [aparenciaAberta, setAparenciaAberta] = useState(false);

    const isEditAction = !!changelog;
    const {
        register,
        handleSubmit,
        reset,
        control,
        watch,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        mode: 'onChange'
    });

    const formValues = watch();

    const requiredFieldsFilled =
        formValues.nome &&
        formValues.tipo &&
        formValues.id_sistema &&
        formValues.data;

    const tipoSelecionado = TIPOS_NOTIFICACAO[formValues.tipo] ?? TIPOS_NOTIFICACAO.Funcionalidade;

    const { data: listaSystemsData } = useQuery({
        queryKey: ['listaSistemas'],
        queryFn: () => listaSistemas(),
    });
    const systems = listaSystemsData?.data?.data ?? [];

    const valoresPrevia = useMemo(() => {
        if (!open) return changelog;

        const preenchidos = Object.fromEntries(
            Object.entries(formValues).filter(([_, v]) => v !== undefined && v !== "")
        );

        const sistema = systems.find((s) => String(s.id) === String(formValues.id_sistema)) ?? changelog?.sistema;

        return {
            ...changelog,
            ...preenchidos,
            sistema,
        };
    }, [open, formValues, changelog, systems]);

    const { mutateAsync: manipulaChangelogFn } = useMutation({
        mutationFn: (data) => manipulaChangelog(data, isEditAction),
    });

    async function handleFormSubmit(data) {
        try {
            const formData = new FormData();

            Object.keys(data).forEach(key => {
                const valor = data[key];

                if (key === "arquivo" && selectedFile) {
                    formData.append("arquivo", selectedFile, selectedFile.name);
                } else if (valor instanceof dayjs) {
                    formData.append(key, valor.format("YYYY-MM-DD"));
                } else if (key === "estilo") {
                    formData.append("estilo", temPersonalizacao(valor) ? JSON.stringify(valor) : "");
                } else {
                    formData.append(key, valor ?? "");
                }
            });

            if (isEditAction) {
                formData.append("id", changelog.id);
            }

            const response = await manipulaChangelogFn(formData);

            await queryClient.invalidateQueries({ queryKey: [queryKey[0]] });
            exibirAlerta("Sucesso", response.data.message, "success");
            setOpen(false);
        } catch (error) {
            console.error(error);
            exibirAlerta("Erro", "Erro ao salvar a alteração", "error");
        }
    }

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setValue('arquivo', file, { shouldDirty: true });
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const file = event.dataTransfer.files?.[0];
        if (file) {
            setSelectedFile(file);
            setValue('arquivo', file, { shouldDirty: true });
        }
    };

    const handleRemoveFile = (event) => {
        event.stopPropagation();
        setSelectedFile(null);
        setExistingFile(null);
        setValue('arquivo', null, { shouldDirty: true });
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    useEffect(() => {
        if (!open) {
            ultimaAbertura.current = null;
            reset({});
            setSelectedFile(null);
            setExistingFile(null);
            return;
        }

        const chave = changelog?.id ?? 'nova';

        if (ultimaAbertura.current === chave) return;

        ultimaAbertura.current = chave;

        const estilo = changelog?.estilo ?? {};

        reset({
            id: changelog ? changelog.id : "",
            id_sistema: changelog ? changelog.sistema?.id ?? "" : "",
            nome: changelog ? changelog.nome : "",
            descricao: changelog ? changelog.descricao ?? "" : "",
            versao: changelog ? changelog.versao ?? "" : "",
            tipo: changelog ? changelog.tipo : "Funcionalidade",
            observacao: changelog ? changelog.observacao ?? "" : "",
            data: changelog ? changelog.data : "",
            estilo,
            arquivo: null,
        });

        setAparenciaAberta(temPersonalizacao(estilo));
        setExistingFile(changelog?.arquivo ?? null);
        setSelectedFile(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [changelog, open, reset]);

    const displayFileName = selectedFile ? selectedFile.name : existingFile;
    const hasFile = selectedFile || existingFile;

    return (
        <>
            <Dialog open={open} fullWidth maxWidth={"md"}>
                <form onSubmit={handleSubmit(handleFormSubmit)}>
                    {isEditAction ? (
                        <DialogTitle>
                            Editando Alteração: <strong>{changelog.nome}</strong>
                        </DialogTitle>
                    ) : (
                        <DialogTitle>Cadastrando nova Alteração</DialogTitle>
                    )}
                    <DialogContent>
                        <DialogContentText sx={{ mb: 2 }}>
                            Preencha corretamente os dados
                        </DialogContentText>
                    </DialogContent>

                    <DialogContent>
                        <Box sx={{ p: 2, backgroundColor: "#fafafa", borderRadius: 2 }}>
                            <FormControl variant="standard" fullWidth>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            autoFocus
                                            InputLabelProps={{ shrink: true }}
                                            margin="dense"
                                            label="Nome"
                                            placeholder="Digite o Nome da Alteração"
                                            fullWidth
                                            {...register("nome", { required: "Nome é obrigatório" })}
                                            error={!!errors.nome}
                                            helperText={getFormErrorMessage(errors, "nome")}
                                            variant="outlined"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            margin="dense"
                                            label="Versão"
                                            placeholder="Digite a Versão"
                                            fullWidth
                                            {...register("versao")}
                                            variant="outlined"
                                            error={!!errors.versao}
                                            helperText={getFormErrorMessage(errors, "versao")}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <Controller
                                            name="tipo"
                                            control={control}
                                            defaultValue={changelog?.tipo || "Funcionalidade"}
                                            rules={{ required: "Tipo é obrigatório" }}
                                            render={({ field }) => (
                                                <TextField
                                                    select
                                                    label="Tipo"
                                                    margin="dense"
                                                    fullWidth
                                                    {...field}
                                                    error={!!errors.tipo}
                                                    helperText={getFormErrorMessage(errors, "tipo")}
                                                >
                                                    <MenuItem value="Funcionalidade">Funcionalidade</MenuItem>
                                                    <MenuItem value="Ajuste">Ajuste</MenuItem>
                                                    <MenuItem value="Alerta">Alerta</MenuItem>
                                                </TextField>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <Controller
                                            name="id_sistema"
                                            control={control}
                                            defaultValue={changelog?.sistema?.id || ''}
                                            rules={{ required: "Sistema é obrigatório" }}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    select
                                                    fullWidth
                                                    sx={{ mt: 1 }}
                                                    label="Sistema"
                                                    error={!!errors.id_sistema}
                                                    helperText={getFormErrorMessage(errors, "id_sistema")}
                                                >
                                                    {systems.length > 0 ? (
                                                        [
                                                            systems.filter(option => option.status === 0).map((option) => (
                                                                <MenuItem key={option.id} value={option.id} disabled>
                                                                    {option.nome}
                                                                </MenuItem>
                                                            )),
                                                            systems.filter(option => option.status !== 0).map((option) => (
                                                                <MenuItem key={option.id} value={option.id}>
                                                                    {option.nome}
                                                                </MenuItem>
                                                            )),
                                                        ]
                                                    ) : (
                                                        <MenuItem disabled>Nenhum disponível</MenuItem>
                                                    )}
                                                </TextField>
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <Controller
                                            name="data"
                                            control={control}
                                            defaultValue={changelog?.data ?? ""}
                                            rules={{ required: "Data é obrigatória" }}
                                            render={({ field }) => (
                                                <DatePicker
                                                    label="Data"
                                                    value={field.value ? dayjs(field.value, "DD/MM/YYYY") : null}
                                                    onChange={(newValue) =>
                                                        field.onChange(newValue ? newValue.format("DD/MM/YYYY") : "")
                                                    }
                                                    slotProps={{
                                                        textField: {
                                                            fullWidth: true,
                                                            margin: "dense",
                                                            error: !!errors.data,
                                                            helperText: getFormErrorMessage(errors, "data"),
                                                        },
                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                            Descrição *
                                        </Typography>
                                        <Controller
                                            name="descricao"
                                            control={control}
                                            defaultValue=""
                                            rules={{ required: "Descrição é obrigatória" }}
                                            render={({ field }) => (
                                                <EditorTexto
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    placeholder="Digite a Descrição"
                                                />
                                            )}
                                        />
                                        {errors.descricao && (
                                            <FormHelperText error>{getFormErrorMessage(errors, "descricao")}</FormHelperText>
                                        )}
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            margin="dense"
                                            label="Observação"
                                            placeholder="Digite a Observação"
                                            fullWidth
                                            {...register("observacao")}
                                            variant="outlined"
                                            multiline
                                            rows={2}
                                            error={!!errors.observacao}
                                            helperText={getFormErrorMessage(errors, "observacao")}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Accordion
                                            disableGutters
                                            elevation={0}
                                            expanded={aparenciaAberta}
                                            onChange={(_, aberto) => setAparenciaAberta(aberto)}
                                            sx={{ border: 1, borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}
                                        >
                                            <AccordionSummary expandIcon={<ExpandMore />}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Palette fontSize="small" color="primary" />
                                                    <Typography variant="body2" fontWeight={600}>
                                                        Personalizar aparência
                                                    </Typography>
                                                    {temPersonalizacao(formValues.estilo) && (
                                                        <Typography variant="caption" color="primary">
                                                            · personalizada
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails sx={{ pt: 0 }}>
                                                <Controller
                                                    name="estilo"
                                                    control={control}
                                                    defaultValue={{}}
                                                    render={({ field }) => (
                                                        <Aparencia
                                                            value={field.value}
                                                            onChange={field.onChange}
                                                            corPadrao={tipoSelecionado.color}
                                                            rotuloPadrao={tipoSelecionado.label}
                                                        />
                                                    )}
                                                />
                                            </AccordionDetails>
                                        </Accordion>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Box
                                            sx={{
                                                border: "2px dashed #ccc",
                                                borderRadius: 2,
                                                p: 4,
                                                textAlign: "center",
                                                cursor: "pointer",
                                                position: "relative",
                                                backgroundColor: hasFile ? "#f5f5f5" : "transparent",
                                                "&:hover": { borderColor: "primary.main" },
                                            }}
                                            onClick={() => fileInputRef.current?.click()}
                                            onDragOver={handleDragOver}
                                            onDrop={handleDrop}
                                        >
                                            {hasFile && (
                                                <IconButton
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        color: grey[600],
                                                    }}
                                                    onClick={handleRemoveFile}
                                                    size="medium"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            )}

                                            {hasFile ? (
                                                <>
                                                    <InsertDriveFile color="primary" sx={{ fontSize: 40 }} />
                                                    <Typography variant="body1" sx={{ mt: 1, mb: 1 }}>
                                                        {displayFileName}
                                                    </Typography>
                                                    {!selectedFile && existingFile && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                                            Arquivo atual
                                                        </Typography>
                                                    )}
                                                    <Button variant="outlined" color="primary">
                                                        Alterar Arquivo
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <UploadFileIcon color="primary" sx={{ fontSize: 40 }} />
                                                    <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
                                                        Arraste ou clique para adicionar um arquivo
                                                    </Typography>
                                                    <Button variant="contained" color="primary">
                                                        Selecionar Arquivo
                                                    </Button>
                                                </>
                                            )}

                                            <input
                                                type="file"
                                                hidden
                                                ref={fileInputRef}
                                                onChange={handleFileChange}
                                                accept=".pdf,.doc,.docx,.png,.jpg,.apk"
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            fullWidth
                                            startIcon={<Visibility />}
                                            onClick={() => setPreviewOpen(true)}
                                            disabled={!requiredFieldsFilled}
                                            sx={{ mt: 1 }}
                                        >
                                            Pré-visualizar Notificação
                                        </Button>
                                        {!requiredFieldsFilled && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                                                Preencha todos os campos obrigatórios para visualizar a notificação
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </FormControl>
                        </Box>
                    </DialogContent>

                    <CommonDialogActionsForm
                        errors={errors}
                        isLoading={isSubmitting}
                        setOpen={setOpen}
                        disableSave={!requiredFieldsFilled}
                    />
                </form>
            </Dialog>

            <NotificationCard
                systems={systems}
                formValues={valoresPrevia}
                previewOpen={previewOpen}
                setPreviewOpen={setPreviewOpen}
            />
        </>
    );
}